import { createTheme } from '@mui/material/styles';
import { GLASS } from '@/lib/glassTokens';

/**
 * Radius scale. Values are px strings so they mean the same thing in `sx` and
 * in a plain `style` object — the `sx` numeric shorthand multiplies by
 * `shape.borderRadius`, a plain style object does not, so a bare `borderRadius:
 * 2` means 16px in one and 2px in the other.
 */
const RADII = {
  sm: '6px',
  md: '12px',
  pill: '9999px',
} as const;

/**
 * Neutral ramp (slate) — the values already in the codebase. The `text`,
 * `background` and `surface` entries below are picked out of this ramp rather
 * than defined alongside it.
 */
const SLATE = {
  50: '#f8fafc',
  100: '#f1f5f9',
  200: '#e2e8f0',
  300: '#cbd5e1',
  400: '#94a3b8',
  500: '#64748b',
  600: '#475569',
  700: '#334155',
  800: '#1e293b',
  900: '#0f172a',
} as const;

/**
 * Categorical accents. Not semantic: components use these to tell entities apart
 * (metric cards, chart series, status pills), so they hold one value in both
 * schemes. Staying mode-invariant is what lets the literals be swapped for the
 * token without changing a render.
 */
const ACCENT = {
  blue: '#3b82f6',
  indigo: '#6366f1',
  indigoLight: '#818cf8',
  violet: '#8b5cf6',
  purple: '#a855f7',
  cyan: '#06b6d4',
  orange: '#f97316',
  orangeLight: '#fb923c',
  emerald: '#10b981',
  pink: '#ec4899',
} as const;

/**
 * Status colours are mode-invariant on `main`: the app renders #22c55e / #ef4444
 * / #f59e0b in both schemes today and `main` preserves exactly that. The
 * brighter dark ramp globals.css declares (#4ade80 / #f87171 / #fbbf24) stays
 * reachable as `light`, and the deeper ramp as `dark`, so neither is lost.
 */
const STATUS = {
  success: { main: '#22c55e', light: '#4ade80', dark: '#15803d' },
  warning: { main: '#f59e0b', light: '#fbbf24', dark: '#d97706' },
  error: { main: '#ef4444', light: '#f87171', dark: '#dc2626' },
  info: { main: '#0ea5e9', light: '#38bdf8', dark: '#0284c7' },
} as const;

declare module '@mui/material/styles' {
  interface Palette {
    accent: typeof ACCENT;
    surface: { raised: string; sunken: string; border: string };
  }
  interface PaletteOptions {
    accent?: typeof ACCENT;
    surface?: { raised: string; sunken: string; border: string };
  }
  interface Theme {
    glass: typeof GLASS;
    radii: typeof RADII;
  }
  interface ThemeOptions {
    glass?: typeof GLASS;
    radii?: typeof RADII;
  }
}

export const theme = createTheme({
  cssVariables: {
    // ThemeContext already stamps `data-theme="light|dark"` on <html>; key the
    // generated custom properties off that attribute instead of adding a second
    // switch that could drift out of step with it.
    colorSchemeSelector: 'data-theme',
  },
  defaultColorScheme: 'dark',
  colorSchemes: {
    light: {
      palette: {
        primary: { main: '#2563eb', light: '#3b82f6', dark: '#1d4ed8' },
        secondary: { main: '#2563eb', light: '#3b82f6', dark: '#1d4ed8' },
        ...STATUS,
        background: { default: SLATE[50], paper: '#ffffff' },
        text: { primary: SLATE[900], secondary: SLATE[500] },
        accent: ACCENT,
        surface: { raised: '#ffffff', sunken: SLATE[100], border: SLATE[200] },
        grey: {
          50: SLATE[50], 100: SLATE[100], 200: SLATE[200], 300: SLATE[300],
          400: SLATE[400], 500: SLATE[500], 600: SLATE[600], 700: SLATE[700],
          800: SLATE[800], 900: SLATE[900],
        },
        divider: 'rgba(0,0,0,0.12)',
      },
    },
    dark: {
      palette: {
        primary: { main: '#60a5fa', light: '#93c5fd', dark: '#2563eb' },
        secondary: { main: '#60a5fa', light: '#93c5fd', dark: '#2563eb' },
        ...STATUS,
        background: { default: SLATE[900], paper: SLATE[800] },
        text: { primary: SLATE[50], secondary: SLATE[400] },
        accent: ACCENT,
        surface: { raised: SLATE[800], sunken: SLATE[900], border: SLATE[700] },
        grey: {
          50: SLATE[800], 100: SLATE[700], 200: SLATE[600], 300: SLATE[500],
          400: SLATE[400], 500: SLATE[300], 600: SLATE[200], 700: SLATE[100],
          800: SLATE[50], 900: '#ffffff',
        },
        divider: 'rgba(148,163,184,0.12)',
      },
    },
  },
  glass: GLASS,
  radii: RADII,
  typography: {
    fontFamily: '"Inter", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    fontWeightLight: 300,
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightBold: 600,
  },
  shape: { borderRadius: 8 },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          // MUI keys a translucent white gradient to elevation on every
          // Paper-derived surface (Card, Dialog, Menu, Popover, AppBar), which
          // is what makes the glass backgrounds fight the theme in dark mode.
          backgroundImage: 'unset',
        },
      },
      defaultProps: { elevation: 1 },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: 'transparent',
          boxShadow: 'none',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: RADII.sm,
          textTransform: 'none',
          fontWeight: 600,
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: ({ theme: t }) => ({
          backgroundColor: SLATE[700],
          color: SLATE[50],
          fontSize: '0.75rem',
          padding: '6px 12px',
          borderRadius: RADII.sm,
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
          ...t.applyStyles('dark', {
            backgroundColor: SLATE[800],
            border: '1px solid rgba(148,163,184,0.2)',
          }),
        }),
        arrow: ({ theme: t }) => ({
          color: SLATE[700],
          ...t.applyStyles('dark', { color: SLATE[800] }),
        }),
      },
    },
  },
});
